// §8 Step 2 — Personal details. Nothing pre-selected; all errors at once on
// Continue; errors clear on edit. In mock-flow, Aadhaar only needs to be
// a 12-digit test value; the real API branch keeps backend validation.
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import Input from '@/components/ui/Input/Input';
import RadioInput from '@/components/ui/RadioInput/RadioInput';
import OnboardingLayout from './components/OnboardingLayout';
import StepGuard from './components/StepGuard';
import { useOnboarding } from '@/context/OnboardingContext';
import { createApplication, OnboardingApiError } from '@/api/onboarding';
import { isRealApiEnabled } from '@/api/client';
import { setApplicationId } from '@/api/session';
import './onboarding.css';

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const EMAIL_REGEX = /^[\w\-.]+@([\w-]+\.)+[a-zA-Z]{2,}$/;

// Verhoeff checksum — every real Aadhaar passes this; random 12-digit numbers
// almost never do. The backend rejects them with ERR_006, so catch them here.
const VERHOEFF_D = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];
const VERHOEFF_P = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

const verhoeffValid = (digits: string): boolean => {
  let c = 0;
  for (let i = 0; i < digits.length; i += 1) {
    c = VERHOEFF_D[c][VERHOEFF_P[i % 8][Number(digits[digits.length - 1 - i])]];
  }
  return c === 0;
};

type Errors = Record<string, string>;

const PersonalDetailsForm = () => {
  const navigate = useNavigate();
  const { draft, updatePersonal, markPersonalSaved } = useOnboarding();
  const p = draft.personal;
  const [errors, setErrors] = useState<Errors>({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const set = (patch: Partial<typeof p>) => {
    updatePersonal(patch);
    const key = Object.keys(patch)[0];
    setErrors((e) => ({ ...e, [key]: '' }));
  };

  const aadhaarDigits = p.aadhaar.replace(/\s/g, '');
  const aadhaarValid = isRealApiEnabled()
    ? /^[2-9]\d{11}$/.test(aadhaarDigits) && verhoeffValid(aadhaarDigits)
    : /^\d{12}$/.test(aadhaarDigits);

  const dobValid = (): boolean => {
    const d = parseInt(p.dobDay, 10);
    const m = parseInt(p.dobMonth, 10);
    const y = parseInt(p.dobYear, 10);
    if (!d || !m || !y || p.dobYear.length !== 4) return false;
    const date = new Date(y, m - 1, d);
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return false;
    return date.getTime() <= Date.now();
  };

  const age = (): number => {
    const birth = new Date(parseInt(p.dobYear, 10), parseInt(p.dobMonth, 10) - 1, parseInt(p.dobDay, 10));
    const now = new Date();
    let a = now.getFullYear() - birth.getFullYear();
    if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) a--;
    return a;
  };

  const validate = (): boolean => {
    const e: Errors = {};
    if (!p.salutation) e.salutation = 'Select a salutation.';
    if (!p.gender) e.gender = 'Select your gender.';
    if (!p.firstAndMiddleNames.trim()) e.firstAndMiddleNames = 'Enter your first and middle names as per your ID.';
    if (!p.surname.trim()) e.surname = 'Enter your surname as per your ID.';
    if (!p.dobDay || !p.dobMonth || !p.dobYear) {
      e.dob = 'Enter your date of birth (DD MM YYYY).';
    } else if (!dobValid()) {
      e.dob = 'Enter a valid date of birth.';
    } else if (age() < 18) {
      e.dob = 'You must be 18 or older to apply online — accounts for minors are opened at a NovaBank branch with a parent or guardian.';
    }
    if (aadhaarDigits.length !== 12) {
      e.aadhaar = 'Enter your 12-digit Aadhaar number.';
    } else if (!aadhaarValid) {
      e.aadhaar = "This doesn't look right — please check the digits.";
    }
    if (!PAN_REGEX.test(p.pan)) e.pan = 'PAN looks like ABCDE1234F — 5 letters, 4 digits, 1 letter.';
    if (!EMAIL_REGEX.test(p.email)) e.email = 'Enter a valid email address.';
    if (!/^\d{10}$/.test(p.mobile)) e.mobile = 'Enter a valid 10-digit mobile number.';
    if (!p.residentOfIndia) e.residentOfIndia = 'Please select Yes or No.';
    setErrors(e);
    return Object.values(e).every((v) => !v);
  };

  const handleContinue = async () => {
    if (!validate()) return;
    setLoading(true);
    setSubmitError('');
    try {
      // Creates the application and sends both OTPs (steps 1-2 endpoint).
      const app = await createApplication({
        product: draft.product as string,
        personal: p,
        consentsAccepted: draft.consentsAccepted,
      });
      setApplicationId(app.applicationId);
      markPersonalSaved();
      navigate('/open-account/sms-verification');
    } catch (err) {
      setSubmitError(
        err instanceof OnboardingApiError ? err.message : 'Could not start your application. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const formatAadhaar = (value: string) =>
    value.replace(/\D/g, '').slice(0, 12).replace(/(\d{4})(?=\d)/g, '$1 ');

  const maskedAadhaar = `XXXX XXXX ${aadhaarDigits.slice(-4)}`;

  return (
    <OnboardingLayout step={2} heading="Let's get started" backTo="/open-account">
      {/* 1 Salutation */}
      <div className="nova-ob-section">
        <div className="nova-ob-label">Salutation</div>
        <select
          className={`form-select ${errors.salutation ? 'is-invalid' : ''}`}
          value={p.salutation}
          onChange={(e) => set({ salutation: e.target.value })}
        >
          <option value="">Select</option>
          {['Mr', 'Mrs', 'Ms', 'Dr'].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        {errors.salutation && <div className="invalid-feedback d-block">{errors.salutation}</div>}
      </div>

      {/* 2 Gender */}
      <div className="nova-ob-section">
        <div className="nova-ob-label">Gender (as per ID)</div>
        <div className="d-flex flex-column gap-2">
          {[
            { value: 'male', label: 'Male' },
            { value: 'female', label: 'Female' },
            { value: 'other', label: 'Other' },
          ].map((g) => (
            <RadioInput
              key={g.value}
              name="gender"
              value={g.value}
              label={g.label}
              checked={p.gender === g.value}
              onChange={(v) => set({ gender: v })}
            />
          ))}
        </div>
        {errors.gender && <div className="invalid-feedback d-block">{errors.gender}</div>}
      </div>

      {/* 3-4 Names */}
      <Input
        id="firstAndMiddleNames"
        label="First and middle names (as per ID)"
        placeholder="e.g. Rahul Kumar"
        value={p.firstAndMiddleNames}
        error={errors.firstAndMiddleNames}
        onChange={(e) => set({ firstAndMiddleNames: e.target.value })}
      />
      <Input
        id="surname"
        label="Surname (as per ID)"
        placeholder="e.g. Sharma"
        value={p.surname}
        error={errors.surname}
        onChange={(e) => set({ surname: e.target.value })}
      />

      {/* 5 DOB */}
      <div className="nova-ob-section">
        <div className="nova-ob-label">Date of birth (as per ID)</div>
        <div className="d-flex gap-2">
          <input
            ref={dayRef}
            className={`form-control text-center ${errors.dob ? 'is-invalid' : ''}`}
            placeholder="DD"
            inputMode="numeric"
            maxLength={2}
            value={p.dobDay}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 2);
              set({ dobDay: v });
              if (v.length === 2) monthRef.current?.focus();
            }}
          />
          <input
            ref={monthRef}
            className={`form-control text-center ${errors.dob ? 'is-invalid' : ''}`}
            placeholder="MM"
            inputMode="numeric"
            maxLength={2}
            value={p.dobMonth}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 2);
              set({ dobMonth: v });
              if (v.length === 2) yearRef.current?.focus();
            }}
          />
          <input
            ref={yearRef}
            className={`form-control text-center ${errors.dob ? 'is-invalid' : ''}`}
            placeholder="YYYY"
            inputMode="numeric"
            maxLength={4}
            value={p.dobYear}
            onChange={(e) => set({ dobYear: e.target.value.replace(/\D/g, '').slice(0, 4) })}
          />
        </div>
        {errors.dob && <div className="invalid-feedback d-block">{errors.dob}</div>}
        {!errors.dob && dobValid() && (
          <div className="form-text">Age: {age()} years</div>
        )}
      </div>

      {/* 6 Aadhaar — collapses to a card when valid */}
      {aadhaarValid ? (
        <div className="nova-ob-card d-flex justify-content-between align-items-center">
          <div>
            <div className="fw-semibold">Aadhaar · valid format</div>
            <div className="text-muted">{maskedAadhaar}</div>
          </div>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => set({ aadhaar: '' })}
          >
            Change
          </button>
        </div>
      ) : (
        <Input
          id="aadhaar"
          label="Aadhaar number"
          placeholder="XXXX XXXX XXXX"
          inputMode="numeric"
          value={p.aadhaar}
          error={errors.aadhaar}
          helperText={`${aadhaarDigits.length} of 12 digits — kept in memory only, never stored on this device.`}
          onChange={(e) => set({ aadhaar: formatAadhaar(e.target.value) })}
        />
      )}

      {/* 7 PAN */}
      <Input
        id="pan"
        label="PAN"
        placeholder="ABCDE1234F"
        maxLength={10}
        value={p.pan}
        error={errors.pan}
        onChange={(e) => set({ pan: e.target.value.toUpperCase().slice(0, 10) })}
      />

      {/* 8 Email */}
      <Input
        id="email"
        type="email"
        label="Email"
        placeholder="name@example.com"
        value={p.email}
        error={errors.email}
        onChange={(e) => set({ email: e.target.value })}
      />

      {/* 9 Mobile */}
      <div className="nova-ob-section">
        <div className="nova-ob-label">Mobile number</div>
        <div className="input-group">
          <span className="input-group-text">+91</span>
          <input
            className={`form-control ${errors.mobile ? 'is-invalid' : ''}`}
            placeholder="98765 43210"
            inputMode="numeric"
            maxLength={10}
            value={p.mobile}
            onChange={(e) => set({ mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
          />
        </div>
        {errors.mobile && <div className="invalid-feedback d-block">{errors.mobile}</div>}
      </div>

      {/* 10 Resident of India */}
      <div className="nova-ob-section">
        <div className="nova-ob-label">Are you a resident of India?</div>
        <div className="nova-ob-seg">
          {(['YES', 'NO'] as const).map((v) => (
            <button
              key={v}
              type="button"
              className={p.residentOfIndia === v ? 'selected' : ''}
              onClick={() => set({ residentOfIndia: v })}
            >
              {v === 'YES' ? 'Yes' : 'No'}
            </button>
          ))}
        </div>
        {errors.residentOfIndia && <div className="invalid-feedback d-block">{errors.residentOfIndia}</div>}
      </div>

      <div className="nova-ob-info">
        Once you continue, a One-Time Password (OTP) will be sent to both your email and mobile
        number. Please make sure the details are correct — you will need the OTPs on the next
        screen.
      </div>

      {submitError && (
        <div className="alert alert-danger py-2" role="alert">{submitError}</div>
      )}

      <div className="nova-ob-footer">
        <div className="nova-ob-footer-inner">
          <button
            type="button"
            className="btn btn-primary btn-lg w-100 fw-semibold nova-btn"
            disabled={loading}
            onClick={handleContinue}
          >
            {loading && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
            Continue
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
};

const PersonalDetails = () => (
  <StepGuard requires={(d) => !!d.product && d.consentsAccepted}>
    <PersonalDetailsForm />
  </StepGuard>
);

export default PersonalDetails;
